import React from 'react';
import { isNodeSelection } from '@tiptap/react';
import { Editor } from '@tiptap/react';
import { Node } from '@tiptap/pm/model';
import { useRichTextTiptapEditor } from 'apps/sigma2-ui/src/components/rich-text/tiptap/hooks/use-tiptap-editor';
import { richTextFindNodePosition } from 'apps/sigma2-ui/src/components/rich-text/tiptap/lib/tiptap-utils';
import { richTextIsEmptyNode } from 'apps/sigma2-ui/src/components/rich-text/tiptap/lib/tiptap-utils';
import { richTextIsMarkInSchema } from 'apps/sigma2-ui/src/components/rich-text/tiptap/lib/tiptap-utils';
import { RichTextButtonProps } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
// --- Styles ---
import 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/color-highlight-button/color-highlight-button.scss';

('use client');

export const RICH_TEXT_HIGHLIGHT_COLORS = [
  {
    label: 'Default background',
    value: 'var(--tt-bg-color)',
    border: 'var(--tt-bg-color-contrast)',
  },
  {
    label: 'Gray background',
    value: 'var(--tt-color-highlight-gray)',
    border: 'var(--tt-color-highlight-gray-contrast)',
  },
  {
    label: 'Brown background',
    value: 'var(--tt-color-highlight-brown)',
    border: 'var(--tt-color-highlight-brown-contrast)',
  },
  {
    label: 'Orange background',
    value: 'var(--tt-color-highlight-orange)',
    border: 'var(--tt-color-highlight-orange-contrast)',
  },
  {
    label: 'Yellow background',
    value: 'var(--tt-color-highlight-yellow)',
    border: 'var(--tt-color-highlight-yellow-contrast)',
  },
  {
    label: 'Green background',
    value: 'var(--tt-color-highlight-green)',
    border: 'var(--tt-color-highlight-green-contrast)',
  },
  {
    label: 'Blue background',
    value: 'var(--tt-color-highlight-blue)',
    border: 'var(--tt-color-highlight-blue-contrast)',
  },
  {
    label: 'Purple background',
    value: 'var(--tt-color-highlight-purple)',
    border: 'var(--tt-color-highlight-purple-contrast)',
  },
  {
    label: 'Pink background',
    value: 'var(--tt-color-highlight-pink)',
    border: 'var(--tt-color-highlight-pink-contrast)',
  },
  {
    label: 'Red background',
    value: 'var(--tt-color-highlight-red)',
    border: 'var(--tt-color-highlight-red-contrast)',
  },
];

export interface RichTextColorHighlightButtonProps
  extends Omit<RichTextButtonProps, 'type'> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor | null;
  /**
   * The node to apply highlight to
   */
  node?: Node | null;
  /**
   * The position of the node in the document
   */
  nodePos?: number | null;
  /**
   * The color to apply when toggling the highlight.
   * If not provided, it will use the default color from the extension.
   */
  color: string;
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Whether the button should hide when the mark is not available.
   * @default false
   */
  hideWhenUnavailable?: boolean;
  /**
   * Called when the highlight is applied.
   */
  onApplied?: (color: string) => void;
}

/**
 * Checks if highlight can be toggled in the current editor state
 */
export function richTextCanToggleHighlight(editor: Editor | null): boolean {
  if (!editor) return false;
  try {
    return editor.can().setMark('highlight');
  } catch {
    return false;
  }
}

/**
 * Checks if highlight is active in the current selection
 */
export function richTextIsHighlightActive(
  editor: Editor | null,
  color: string,
): boolean {
  if (!editor) return false;
  return editor.isActive('highlight', { color });
}

/**
 * Toggles highlight on the current selection or specified node
 */
export function richTextToggleHighlight(
  editor: Editor | null,
  color: string,
  node?: Node | null,
  nodePos?: number | null,
): void {
  if (!editor) return;

  try {
    const chain = editor.chain().focus();

    if (richTextIsEmptyNode(node)) {
      chain.toggleMark('highlight', { color }).run();
    } else if (nodePos !== undefined && nodePos !== null && nodePos !== -1) {
      chain.setNodeSelection(nodePos).toggleMark('highlight', { color }).run();
    } else if (node) {
      const foundPos = richTextFindNodePosition({ editor, node });
      if (foundPos) {
        chain
          .setNodeSelection(foundPos.pos)
          .toggleMark('highlight', { color })
          .run();
      } else {
        chain.toggleMark('highlight', { color }).run();
      }
    } else {
      chain.toggleMark('highlight', { color }).run();
    }

    editor.chain().setMeta('hideDragHandle', true).run();
  } catch (error) {
    console.error('Failed to apply highlight:', error);
  }
}

/**
 * Determines if the highlight button should be disabled
 */
export function richTextIsColorHighlightButtonDisabled(
  editor: Editor | null,
  userDisabled: boolean = false,
): boolean {
  if (!editor || userDisabled) return true;

  const isIncompatibleContext =
    editor.isActive('code') ||
    editor.isActive('codeBlock') ||
    editor.isActive('imageUpload');

  return isIncompatibleContext || !richTextCanToggleHighlight(editor);
}

/**
 * Determines if the highlight button should be shown
 */
export function richTextShouldShowColorHighlightButton(
  editor: Editor | null,
  hideWhenUnavailable: boolean,
  highlightInSchema: boolean,
): boolean {
  if (!highlightInSchema || !editor) return false;

  if (hideWhenUnavailable) {
    if (
      isNodeSelection(editor.state.selection) ||
      !richTextCanToggleHighlight(editor)
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Custom hook to manage highlight button state
 */
export function useRichTextHighlightState(
  editor: Editor | null,
  color: string,
  disabled: boolean = false,
  hideWhenUnavailable: boolean = false,
) {
  const highlightInSchema = richTextIsMarkInSchema('highlight', editor);
  const isDisabled = richTextIsColorHighlightButtonDisabled(editor, disabled);
  const isActive = richTextIsHighlightActive(editor, color);

  const shouldShow = React.useMemo(
    () =>
      richTextShouldShowColorHighlightButton(
        editor,
        hideWhenUnavailable,
        highlightInSchema,
      ),
    [editor, hideWhenUnavailable, highlightInSchema],
  );

  return {
    highlightInSchema,
    isDisabled,
    isActive,
    shouldShow,
  };
}

/**
 * ColorHighlightButton component for TipTap editor
 */
export const RichTextColorHighlightButton = React.forwardRef<
  HTMLButtonElement,
  RichTextColorHighlightButtonProps
>(
  (
    {
      editor: providedEditor,
      node,
      nodePos,
      color,
      text,
      hideWhenUnavailable = false,
      className = '',
      disabled,
      onClick,
      onApplied,
      children,
      style,
      ...buttonProps
    },
    ref,
  ) => {
    const editor = useRichTextTiptapEditor(providedEditor);
    const { isDisabled, isActive, shouldShow } = useRichTextHighlightState(
      editor,
      color,
      disabled,
      hideWhenUnavailable,
    );

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (!e.defaultPrevented && !isDisabled && editor) {
          richTextToggleHighlight(editor, color, node, nodePos);
          onApplied?.(color);
        }
      },
      [color, editor, isDisabled, node, nodePos, onClick, onApplied],
    );

    const buttonStyle = React.useMemo(
      () =>
        ({
          ...style,
          '--highlight-color': color,
        }) as React.CSSProperties,
      [color, style],
    );

    if (!shouldShow || !editor || !editor.isEditable) {
      return null;
    }

    return (
      <RichTextButton
        type="button"
        className={className.trim()}
        disabled={isDisabled}
        data-style="ghost"
        data-active-state={isActive ? 'on' : 'off'}
        data-disabled={isDisabled}
        role="button"
        tabIndex={-1}
        aria-label={`${color} highlight color`}
        aria-pressed={isActive}
        onClick={handleClick}
        style={buttonStyle}
        {...buttonProps}
        ref={ref}
      >
        {children || (
          <>
            <span
              className="tiptap-button-highlight"
              style={{ '--highlight-color': color } as React.CSSProperties}
            />
            {text && <span className="tiptap-button-text">{text}</span>}
          </>
        )}
      </RichTextButton>
    );
  },
);

RichTextColorHighlightButton.displayName = 'ColorHighlightButton';

export default RichTextColorHighlightButton;
